import React from 'react';
import {
  Button,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ChevronDown, ChevronUp, Search, X } from 'lucide-react';
import { EntityTreeToolbarProps } from '../interfaces';

export const EntityTreeToolbar: React.FC<EntityTreeToolbarProps> = ({
  searchQuery,
  onSearchChange,
  totalEntitiesCount,
  rootEntitiesCount,
  isAllExpanded,
  onToggleExpandAll,
}) => {
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1.25}
      alignItems={{ xs: 'stretch', sm: 'center' }}
      justifyContent="space-between"
      sx={{ mb: 1.25 }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ flex: 1, maxWidth: { sm: 320 } }}
      >
        <TextField
          size="small"
          fullWidth
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search partner entities..."
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" sx={{ mr: 0.5 }}>
                <Search size={14} style={{ color: '#94a3b8' }} />
              </InputAdornment>
            ),
            endAdornment: searchQuery ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => onSearchChange('')}
                  edge="end"
                  aria-label="Clear search"
                  sx={{ color: '#94a3b8', p: 0.25 }}
                >
                  <X size={13} />
                </IconButton>
              </InputAdornment>
            ) : null,
            sx: {
              height: 32,
              borderRadius: '7px',
              bgcolor: '#ffffff',
              fontSize: '0.8rem',
              '& fieldset': {
                borderColor: '#e2e8f0',
              },
              '&:hover fieldset': {
                borderColor: '#cbd5e1',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#3b82f6',
              },
            },
          }}
        />
      </Stack>

      <Stack
        direction="row"
        spacing={1.25}
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography
          variant="body2"
          sx={{ color: '#64748b', fontSize: '0.75rem', fontWeight: 500 }}
        >
          {rootEntitiesCount} {rootEntitiesCount === 1 ? 'partner' : 'partners'}
          {totalEntitiesCount > rootEntitiesCount && (
            <span> ({totalEntitiesCount} total entities)</span>
          )}
        </Typography>

        <Button
          variant="outlined"
          size="small"
          onClick={onToggleExpandAll}
          startIcon={
            isAllExpanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />
          }
          sx={{
            textTransform: 'none',
            fontSize: '0.75rem',
            fontWeight: 600,
            borderRadius: '6px',
            borderColor: '#e2e8f0',
            color: '#475569',
            bgcolor: '#ffffff',
            py: 0.35,
            px: 1.1,
            height: 28,
            '&:hover': {
              borderColor: '#cbd5e1',
              bgcolor: '#f8fafc',
            },
          }}
        >
          {isAllExpanded ? 'Collapse All' : 'Expand All'}
        </Button>
      </Stack>
    </Stack>
  );
};
